import { SurveyTemplate, SurveySection } from '../types/survey.types';

const TEMPLATE_VAR_REGEX = /\{\{([a-zA-Z_][a-zA-Z0-9_ ]*)\}\}/g;

/**
 * Extract all unique variable names from a template's content.
 */
export function extractTemplateVariables(template: SurveyTemplate): string[] {
  const variables = new Set<string>();

  const scan = (text: string | undefined | null) => {
    if (!text) return;
    const regex = new RegExp(TEMPLATE_VAR_REGEX.source, 'g');
    let match;
    while ((match = regex.exec(text)) !== null) {
      variables.add(match[1].trim());
    }
  };

  scan(template.name);
  scan(template.description);
  scan(template.defaultSettings?.thankYouMessage);

  for (const section of template.sections) {
    scan(section.title);
    scan(section.description);

    for (const question of section.questions) {
      scan(question.title);
      scan(question.description);
      scan(question.placeholder);
      scan(question.settings?.helpText);
      question.settings?.scaleLabels?.forEach(label => scan(label));
      question.options?.forEach(opt => {
        scan(opt.label);
        scan(opt.value);
      });
      question.settings?.matrixRows?.forEach(r => { scan(r.label); scan(r.value); });
      question.settings?.matrixColumns?.forEach(c => { scan(c.label); scan(c.value); });
      question.validationRules?.forEach(rule => scan(rule.message));
    }
  }

  return Array.from(variables);
}

/**
 * Get where each variable is used in the template.
 */
export function getVariableLocations(template: SurveyTemplate): Record<string, string[]> {
  const locations: Record<string, string[]> = {};

  const scanField = (text: string | undefined | null, location: string) => {
    if (!text) return;
    const regex = new RegExp(TEMPLATE_VAR_REGEX.source, 'g');
    let match;
    while ((match = regex.exec(text)) !== null) {
      const varName = match[1].trim();
      if (!locations[varName]) locations[varName] = [];
      locations[varName].push(location);
    }
  };

  scanField(template.name, 'Template name');
  scanField(template.description, 'Template description');

  template.sections.forEach((section, sIdx) => {
    const sLabel = `Section ${sIdx + 1}`;
    scanField(section.title, `${sLabel} title`);
    scanField(section.description, `${sLabel} description`);

    section.questions.forEach((q, qIdx) => {
      const qLabel = `${sLabel}, Q${qIdx + 1}`;
      scanField(q.title, `${qLabel} title`);
      scanField(q.description, `${qLabel} description`);
      scanField(q.placeholder, `${qLabel} placeholder`);
      scanField(q.settings?.helpText, `${qLabel} help text`);
      q.options?.forEach((opt, oIdx) => {
        scanField(opt.label, `${qLabel}, option ${oIdx + 1}`);
      });
    });
  });

  return locations;
}

/**
 * Replace all {{variable}} occurrences in a string.
 */
export function substituteVariables(
  text: string,
  values: Record<string, string>
): string {
  return text.replace(TEMPLATE_VAR_REGEX, (match, varName) => {
    const trimmed = varName.trim();
    return values[trimmed] !== undefined ? values[trimmed] : match;
  });
}

/**
 * Deep-substitute variables across all string fields in sections.
 */
export function substituteSectionsVariables(
  sections: SurveySection[],
  values: Record<string, string>
): SurveySection[] {
  const cloned = JSON.parse(JSON.stringify(sections)) as SurveySection[];

  for (const section of cloned) {
    section.title = substituteVariables(section.title, values);
    if (section.description) {
      section.description = substituteVariables(section.description, values);
    }

    for (const question of section.questions) {
      question.title = substituteVariables(question.title, values);
      if (question.description) {
        question.description = substituteVariables(question.description, values);
      }
      if (question.placeholder) {
        question.placeholder = substituteVariables(question.placeholder, values);
      }
      if (question.settings?.helpText) {
        question.settings.helpText = substituteVariables(question.settings.helpText, values);
      }
      if (question.settings?.scaleLabels) {
        question.settings.scaleLabels = question.settings.scaleLabels.map(
          l => substituteVariables(l, values)
        );
      }
      if (question.options) {
        for (const opt of question.options) {
          opt.label = substituteVariables(opt.label, values);
          opt.value = substituteVariables(opt.value, values);
        }
      }
      if (question.settings?.matrixRows) {
        for (const r of question.settings.matrixRows) {
          r.label = substituteVariables(r.label, values);
          r.value = substituteVariables(r.value, values);
        }
      }
      if (question.settings?.matrixColumns) {
        for (const c of question.settings.matrixColumns) {
          c.label = substituteVariables(c.label, values);
          c.value = substituteVariables(c.value, values);
        }
      }
      if (question.validationRules) {
        for (const rule of question.validationRules) {
          rule.message = substituteVariables(rule.message, values);
        }
      }
    }
  }

  return cloned;
}

/**
 * Convert a variable name to a human-readable label.
 */
export function variableNameToLabel(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, c => c.toUpperCase());
}
